"use client";

import React, { useState, useEffect } from "react";
import { useSession } from "next-auth/react";
import { useLoginNavigation } from "@/hooks/login/useLoginNavigation";
import mappedLoginNavigation, { IMappedLoginNavigation } from "@/models/login/login-navigation-mapper";
import { useUserGroups } from "@/hooks/login/useUserGroups";
import { AuthStatusProvider, useAuthStatusContext } from "@/providers/AuthStatusProvider";
import LoggedInNavItem from "./logged-in-nav-item";
import LoggedOutNavItem from "./logged-out-nav-item";

const LoginNavItemContent = () => {
  const { isAuthenticated, checkAuth } = useAuthStatusContext();
  const { data: session, status: sessionStatus } = useSession();
  const { data } = useLoginNavigation();
  const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null);
  const [popoverType, setPopoverType] = useState<"menu" | "login" | null>(null);
  const [isScrolled, setIsScrolled] = useState(false);

  // Use our hook to get user groups client-side
  const { refreshUserGroups, userGroups } = useUserGroups();

  // Refresh user groups when session changes (e.g., after login)
  useEffect(() => {
    if (sessionStatus === "authenticated") {
      // Add async function to properly await the refresh
      const updateGroups = async () => {
        await refreshUserGroups();
      };
      updateGroups();
    }
  }, [sessionStatus, refreshUserGroups]);

  useEffect(() => {
    const handleScroll = () => setIsScrolled(window.scrollY > 0);
    handleScroll();
    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const handleClick = async (event: React.MouseEvent<HTMLButtonElement>, type: "menu" | "login") => {
    setAnchorEl(event.currentTarget);
    setPopoverType(type);

    // Refresh user groups when the menu is opened
    if (type === "menu" && sessionStatus === "authenticated" && userGroups.length === 0) {
      await refreshUserGroups();
      // eslint-disable-next-line no-console
      console.log("User groups refreshed on menu click:", userGroups);
    }
  };

  // Optionally, re-check auth after login popover closes
  const handleClose = () => {
    setAnchorEl(null);
    setPopoverType(null);
    checkAuth(); // Re-check auth state after popover closes
  };

  const logout = async () => {
    const originalSiteOrigin = sessionStorage.getItem("originalSiteOrigin");
    const logoutUrl = originalSiteOrigin
      ? `/logout?originalSiteOrigin=${encodeURIComponent(originalSiteOrigin)}`
      : "/logout";
    window.location.href = logoutUrl;
  };

  if (!data) return null;

  const mappedData: IMappedLoginNavigation = mappedLoginNavigation(data);

  // Provide a minimal fallback session object to satisfy LoggedInNavItem props
  const fallbackSession = { idToken: "", expires: "" };

  return (
    <div data-testid="login-nav-item">
      {isAuthenticated ? (
        <LoggedInNavItem
          session={session ?? fallbackSession}
          mappedData={mappedData}
          anchorEl={anchorEl}
          popoverType={popoverType}
          handleClick={handleClick}
          handleClose={handleClose}
          logout={logout}
          userGroups={userGroups}
          isScrolled={isScrolled}
          data-testid="logged-in-nav-item"
        />
      ) : (
        <LoggedOutNavItem
          mappedData={mappedData}
          anchorEl={anchorEl}
          popoverType={popoverType}
          handleClick={handleClick}
          handleClose={handleClose}
          isScrolled={isScrolled}
        />
      )}
    </div>
  );
};

const LoginNavItem = () => (
  <AuthStatusProvider>
    <LoginNavItemContent />
  </AuthStatusProvider>
);

export default LoginNavItem;
